import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MetaDataUtils } from '../../../meta-data/meta-data-utils';
import { MessageResourceManager } from '../../../resources/message-resource-manager';
import { ExternalFieldComponent } from '../core/external-field/external-field.component';
import { merge, Subscription } from 'rxjs';
import { LabelBoxComponent } from '../core/base/label-box/label-box.component';
import { ExtNewAutocompleteTextBoxComponent } from '../core/ext-new-autocomplete-text-box/ext-new-autocomplete-text-box.component';
import { SimpleFieldMetaData } from '../../../layout-meta-data';
import type { BaseErrorInterface, ExternalViewModelInterface, MetaDataDescriptionsInterface, PropertyViewModelInterface } from '@nts/std/interfaces';
import { ViewReadyCoordinator } from '../core/view-ready-coordinator';
import { BaseError } from '@nts/std/messages';

@UntilDestroy()
@Component({
  selector: 'nts-decode-text-box',
  templateUrl: './decode-text-box.component.html',
  styleUrls: ['./decode-text-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    LabelBoxComponent,
    ExtNewAutocompleteTextBoxComponent,
  ]
})
export class DecodeTextBoxComponent extends ExternalFieldComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  /**
   * Property valorizzata solo dal layout custom, vincerà sul externalPropertyViewModel.isVisible solo se sarà impostata a false
   */
  @Input() override isHidden = false;

  @HostBinding('class.additional-field')
  @Input() override additionalField = false;

  @HostBinding('class.full-column')
  @Input() fullColumn = false;

  @Input() externalPropertyViewModel!: ExternalViewModelInterface;

  /**
   * @deprecated
   * Utilizza [decodeProperties] per elencare la lista delle decode properties
   */
  @Input() decodeDescription?: PropertyViewModelInterface;

  /**
   * @deprecated
   * Aggiungi in [decodeProperties] il codice se vuoi visualizzarlo
   */
  @Input() showCodeInDescription: boolean | null = null;

  /**
   * @deprecated
   * Il codice viene automaticamente recuperato dai metadati
   */
  @Input() code?: PropertyViewModelInterface | null;

  /**
   * Lista dei campi in Pascal Case, si possono anche passare i custom property view model (sempre in Pascal Case)
   */
  @Input() decodeProperties: string[] = null;

  /**
   * Lista dei campi dove effettuare la ricerca in Pascal Case
   */
  @Input() searchProperties: string[] = null;

  /**
   * Stringa da utilizzare come separatore
   */
  @Input() fieldSeparator: string = null;

  /**
   * Al momento supporta solo la stringa 'top', permette di impostare la label sopra il campo
   */
  @Input() labelPosition = '';

  @Input() isDisabled: boolean = false;
  @Input() tabIndex!: number;
  @Input() isLabelZoomEnabled = true;
  @Input() appendTo = null;
  @Input() enableOutsideClickListener = false;

  @Input() primaryColor = null;

  /**
   * Property per ridefinire il path per il layout.
   */
  @Input() customPathForLayout: string = null;

  @Output() externalPropertyViewModelInitialized = new EventEmitter<void>();
  readonly viewReady = output<void>();


  isViewReady = false;
  private _textBox: ExtNewAutocompleteTextBoxComponent | null = null;

  @ViewChild('textBox', { static: false })
  set textBox(component: ExtNewAutocompleteTextBoxComponent | null) {
    this._textBox = component;
    this.tryEmitViewReady();
  }
  get textBox(): ExtNewAutocompleteTextBoxComponent | null {
    return this._textBox;
  }

  /**
   * Property calcolata in base alle due property: isHidden e externalPropertyViewModel.isVisible
   */
  @HostBinding('class.is-hidden')
  isHiddenCalculated = false;

  loadingError: BaseErrorInterface = null;

  get isFullWidth() {
    return (this.labelPosition === 'top' ? true : false);
  }

  private nameFromInput = undefined;

  @Input()
  set name(value: string) {
    this.nameFromInput = value;
  }
  get name(): string {
    return this.nameFromInput ?? MetaDataUtils.toCamelCase(this.externalPropertyViewModel?.externalMetaData.principalPropertyName);
  }

  private pathFromInput = undefined;

  @Input()
  set path(value: string) {
    this.pathFromInput = value;
  }
  get path(): string {
    if (this.pathFromInput != null) {
      return this.pathFromInput;
    }
    if (this.customPathForLayout != null) {
      return this.customPathForLayout;
    }
    if (this.externalPropertyViewModel?.customPathForLayout != null) {
      return this.externalPropertyViewModel.customPathForLayout;
    }
    return this.externalPropertyViewModel?.reservedPath;
  }

  get description(): string {
    return this.externalPropertyViewModel?.externalMetaData.descriptions.description?.length > 0 ? this.externalPropertyViewModel?.externalMetaData.descriptions.description : MessageResourceManager.Current.getMessage(this.externalPropertyViewModel?.externalMetaData.descriptions.descriptionKey);
  }

  get displayName(): string {
    return this.externalPropertyViewModel?.externalMetaData.descriptions.displayName?.length > 0 ? this.externalPropertyViewModel?.externalMetaData.descriptions.displayName : MessageResourceManager.Current.getMessage(this.externalPropertyViewModel?.externalMetaData.descriptions.displayNameKey);
  }

  /**
   * Popola le descrizioni mancanti con i valori recuperati dalle risorse
   *
   * @param descriptions
   * @returns
   */
  getResolvedDescriptions(descriptions: MetaDataDescriptionsInterface) {
    return {
      displayNameKey: descriptions.displayNameKey,
      displayName: descriptions.displayName?.length > 0 ? descriptions.displayName : MessageResourceManager.Current.getMessage(descriptions.displayNameKey),
      shortNameKey: descriptions.shortNameKey,
      shortName: descriptions.shortName?.length > 0 ? descriptions.shortName : MessageResourceManager.Current.getMessage(descriptions.shortNameKey),
      descriptionKey: descriptions.descriptionKey,
      description: descriptions.description?.length > 0 ? descriptions.description : MessageResourceManager.Current.getMessage(descriptions.descriptionKey),
    };
  }

  get availableExternalFields(): SimpleFieldMetaData[] {
    return this.externalPropertyViewModel?.externalMetaData.dependentAggregateMetaData.rootMetaData.propertyNames.map((p) => {
      const metaData = this.externalPropertyViewModel?.externalMetaData.dependentAggregateMetaData.rootMetaData.getPropertyMetaData(p);
      if (metaData != null) {
        const field = new SimpleFieldMetaData();
        field.descriptions = this.getResolvedDescriptions(metaData.descriptions);
        field.name = metaData.name;
        return field;
      } else {
        console.error(`metadato di ${p} non trobvato per generare gli ExternalFields di ${this.externalPropertyViewModel.externalMetaData.dependentAggregateMetaData.rootMetaData.fullName}`);
      }
      return null;

    }).filter((f) => f != null);
  }

  protected propertyChangedSubscription: Subscription | null = null;
  private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self', 'child'] as const, () => this.emitViewReady());

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {

    if (!this.externalPropertyViewModel) {
      this.loadingError = new BaseError();
      this.loadingError.description = `Errore caricamento componente ${[this.path, this.name].filter(a => a?.length > 0).join('.')}`;
      this.loadingError.code = 'DecodeTextBox_MissingExternalViewModel';
      return;
    }

    let decodeProperties = null;
    if (this.decodeDescription) {

      if (this.decodeDescription?.isCustom === false) {
        decodeProperties = [this.decodeDescription.propertyMetaData.name];
      } else {
        decodeProperties = [this.decodeDescription.propertyName];
      }

    }
    if (this.decodeProperties?.length > 0) {
      decodeProperties = this.decodeProperties;
    }

    if (this.showCodeInDescription == null) {
      this.showCodeInDescription = this.externalPropertyViewModel.showCode;
    }
    this.decodeProperties = decodeProperties;
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['isHidden']) {
      this.syncHiddenState();
    } else if (changes['externalPropertyViewModel']) {
      this.unsubscribe();

      if (this.externalPropertyViewModel) {
        this.syncHiddenState();

        this.propertyChangedSubscription = merge(
          this.externalPropertyViewModel.externalDomainModelChanged,
          this.externalPropertyViewModel.decodeCompleted,
          this.externalPropertyViewModel.propertyChanged
        ).subscribe(() => {
          this.syncHiddenState();
          this.cd.detectChanges();
        });

        this.externalPropertyViewModelInitialized.emit();
      }

    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.viewReadyCoordinator.markReady('self');
      this.tryEmitViewReady();
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  unsubscribe() {
    if (this.propertyChangedSubscription != null) {
      this.propertyChangedSubscription.unsubscribe();
    }
  }

  onTextBoxViewReady(): void {
    this.viewReadyCoordinator.markReady('child');
    this.tryEmitViewReady();
  }

  onKeyDown(event: any) {
    if (event.ctrlKey && this.externalPropertyViewModel.isEnabled) {
      switch (event.key) {
        case 'F6':
          this.textBox.zoom();
          break;
        case 'F8':
          this.textBox.startPresentation(event);
          break;
      }
    }
  }

  getCodes(): string[] | null {
    return this.externalPropertyViewModel?.domainModelMetaData?.identityNames?.map((i) => MetaDataUtils.toCamelCase(i)) ?? null;
  }

  getCodesPvm(): PropertyViewModelInterface[] | null {
    return this.getCodes()?.map((c) => this.externalPropertyViewModel?.getProperty(c)) ?? null;
  }

  openZoom() {
    if ((this.isDisabled || this.externalPropertyViewModel?.isEnabled === false || !this.getCodesPvm()?.every((c) => c.isEnabled)) && this.externalPropertyViewModel?.securityAccess == null) {
      this.textBox.startPresentation(null, true, false);
      return;
    }
    if (this.externalPropertyViewModel?.securityAccess == null) {
      this.textBox.zoom();
    }
  }

  private tryEmitViewReady(): void {
    this.viewReadyCoordinator.tryEmit(() => this.textBox != null);
  }

  private emitViewReady(): void {
    this.isViewReady = true;
    this.viewReady.emit();
  }

  private syncHiddenState(): void {
    this.isHiddenCalculated = this.isHidden === true ? true : !this.externalPropertyViewModel?.isVisible;
  }
}
